/** Supplier Exceptions screen (Demo Mode only, local-only - no backend persistence yet).
 * Cross-supplier filterable table of governance exceptions - see
 * `src/mocks/data/supplierProfileData.ts` for the seed data shared with the per-supplier
 * Exceptions tab on the Supplier Profile screen. */

import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Accordion, AccordionDetails, AccordionSummary, Button, Chip, Grid, MenuItem, Stack, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import { ExpandLess as ExpandLessIcon, ExpandMore as ExpandMoreIcon } from '@mui/icons-material';
import PageArea from '../../../../components/shared/PageArea';
import PageTitle from '../../../../components/shared/PageTitle';
import { Section } from '../../../../components/ui/Section';
import { TableHeader } from '../../../../components/forms/TableHeader';
import { Pill } from '../../../../components/shared/Pill';
import {
  supplierExceptionsSeed, SupplierExceptionCategory, SupplierExceptionItem, SupplierExceptionSeverity,
} from '../../../../mocks/data/supplierProfileData';
import {
  EXCEPTION_CATEGORY_LABEL, EXCEPTION_SEVERITY_COLOR, EXCEPTION_STATUS_COLOR,
} from '../components/supplierExceptionChips';

const SupplierExceptionsPage = () => {
  const { t } = useTranslation('suppliers');
  const navigate = useNavigate();
  const [exceptions, setExceptions] = useState<SupplierExceptionItem[]>(supplierExceptionsSeed);
  const [categoryFilter, setCategoryFilter] = useState<SupplierExceptionCategory | 'All'>('All');
  const [severityFilter, setSeverityFilter] = useState<SupplierExceptionSeverity | 'All'>('All');
  const [isAdvancedOpen, setIsAdvancedOpen] = useState<boolean>(false);

  const rows = useMemo(
    () => exceptions.filter((exception) => (categoryFilter === 'All' || exception.category === categoryFilter)
      && (severityFilter === 'All' || exception.severity === severityFilter)),
    [exceptions, categoryFilter, severityFilter],
  );

  const updateStatus = (id: number, status: SupplierExceptionItem['status'], owner?: string) => {
    setExceptions((current) => current.map((exception) => (exception.id === id ? { ...exception, status, owner: owner ?? exception.owner } : exception)));
  };

  return (
    <PageArea>
      <PageTitle>{t('supplierExceptionsTitle', 'Supplier Exceptions')}</PageTitle>

      <Typography variant="textSM"
        color="text.secondary"
        sx={{ mt: 2, mb: 4 }}>
        {t(
          'supplierExceptionsIntro',
          'Cross-supplier governance exceptions requiring attention - missing documents, expired certifications, pending renewals, delivery/pricing/catalogue issues, missing coverage and onboarding requests awaiting review.',
        )}
      </Typography>

      <Stack direction="row"
        spacing={1}
        alignItems="center"
        sx={{ mb: 3 }}>
        {(['Critical', 'High', 'Medium', 'Low'] as SupplierExceptionSeverity[]).map((severity) => (
          <Chip key={severity}
            label={severity}
            color={severityFilter === severity ? 'primary' : 'default'}
            onClick={() => setSeverityFilter((prev) => (prev === severity ? 'All' : severity))}
          />
        ))}
      </Stack>

      <Accordion
        variant="borderless"
        expanded={isAdvancedOpen}
        onChange={(_, expanded) => setIsAdvancedOpen(expanded)}
      >
        <AccordionSummary>
          <Grid container
            alignItems="center"
            justifyContent="space-between"
            sx={{ width: '100%' }}>
            <Grid item>
              <Grid container
                spacing={1}
                alignItems="center">
                <Grid item>
                  {t('filters.advanced', 'Advanced filters')}
                </Grid>
                <Grid item
                  style={{ marginTop: '8px' }}>
                  {isAdvancedOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </Grid>
              </Grid>
            </Grid>
            <Grid item>
              <Button
                size="md"
                variant="secondary"
                onClick={() => {
                  setCategoryFilter('All');
                  setSeverityFilter('All');
                }}>
                {t('filters.clear', 'Clear filters')}
              </Button>
            </Grid>
          </Grid>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container
            spacing={3}>
            <Grid item
              lg={3}
              sm={6}
              xs={12}>
              <TextField select
                label={t('category', 'Category')}
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value as SupplierExceptionCategory | 'All')}
                fullWidth
                size="small"
              >
                <MenuItem value="All">{t('allCategories', 'All categories')}</MenuItem>
                {Object.keys(EXCEPTION_CATEGORY_LABEL).map((category) => (
                  <MenuItem key={category}
                    value={category}>{EXCEPTION_CATEGORY_LABEL[category]}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item
              lg={3}
              sm={6}
              xs={12}>
              <TextField select
                label={t('severity', 'Severity')}
                value={severityFilter}
                onChange={(e) => setSeverityFilter(e.target.value as SupplierExceptionSeverity | 'All')}
                fullWidth
                size="small"
              >
                <MenuItem value="All">{t('allSeverities', 'All severities')}</MenuItem>
                {(['Critical', 'High', 'Medium', 'Low'] as SupplierExceptionSeverity[]).map((severity) => (
                  <MenuItem key={severity}
                    value={severity}>{severity}</MenuItem>
                ))}
              </TextField>
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>

      <Section>
        <TableHeader>
          {t('supplierExceptionsTitle', 'Supplier Exceptions')}
          <Pill text={t('exceptionCount', '{{count}} exceptions', { count: rows.length })} />
        </TableHeader>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t('exception', 'Exception')}</TableCell>
                <TableCell>{t('supplier', 'Supplier')}</TableCell>
                <TableCell>{t('severity', 'Severity')}</TableCell>
                <TableCell>{t('category', 'Category')}</TableCell>
                <TableCell>{t('affectedSites', 'Affected sites')}</TableCell>
                <TableCell>{t('owner', 'Owner')}</TableCell>
                <TableCell>{t('dueDate', 'Due date')}</TableCell>
                <TableCell>{t('status', 'Status')}</TableCell>
                <TableCell>{t('lastUpdated', 'Last updated')}</TableCell>
                <TableCell align="right">{t('actions', 'Actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.length === 0 && (
                <TableRow><TableCell colSpan={10}>{t('noExceptions', 'No exceptions match the selected filters.')}</TableCell></TableRow>
              )}
              {rows.map((exception) => (
                <TableRow key={exception.id}>
                  <TableCell>{exception.exception}</TableCell>
                  <TableCell
                    sx={{ cursor: 'pointer', textDecoration: 'underline' }}
                    onClick={() => navigate(`/procurement/suppliers/profile/${exception.supplierId}`)}
                  >
                    {exception.supplierName}
                  </TableCell>
                  <TableCell><Chip label={exception.severity}
                    size="small"
                    color={EXCEPTION_SEVERITY_COLOR[exception.severity]} /></TableCell>
                  <TableCell>{EXCEPTION_CATEGORY_LABEL[exception.category] ?? exception.category}</TableCell>
                  <TableCell>{exception.affectedSites}</TableCell>
                  <TableCell>{exception.owner ?? '-'}</TableCell>
                  <TableCell>{exception.dueDate ?? '-'}</TableCell>
                  <TableCell><Chip label={exception.status}
                    size="small"
                    color={EXCEPTION_STATUS_COLOR[exception.status]} /></TableCell>
                  <TableCell>{exception.lastUpdated}</TableCell>
                  <TableCell align="right">
                    <Stack direction="row"
                      spacing={1}
                      justifyContent="flex-end">
                      <Button size="md"
                        variant="secondary"
                        onClick={() => updateStatus(exception.id, exception.status, 'Procurement')}>
                        {t('assignOwner', 'Assign owner')}
                      </Button>
                      {exception.category === 'MissingDocuments' && (
                        <Button size="md"
                          variant="secondary"
                          onClick={() => updateStatus(exception.id, 'InProgress', 'Compliance')}>
                          {t('requestDocument', 'Request document')}
                        </Button>
                      )}
                      <Button
                        size="md"
                        variant="secondary"
                        onClick={() => navigate(`/procurement/suppliers/profile/${exception.supplierId}`)}
                      >
                        {t('openSupplier', 'Open supplier')}
                      </Button>
                      {exception.status !== 'Waived' && exception.status !== 'Resolved' && (
                        <Button size="md"
                          variant="secondary"
                          onClick={() => updateStatus(exception.id, 'Waived')}>{t('waive', 'Waive')}</Button>
                      )}
                      {exception.status !== 'Resolved' && (
                        <Button size="md"
                          variant="primary"
                          onClick={() => updateStatus(exception.id, 'Resolved')}>{t('resolve', 'Resolve')}</Button>
                      )}
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Section>
    </PageArea>
  );
};

export default SupplierExceptionsPage;
